/**
 * GA4 analytics wrapper for Quantum Genesis Labs.
 *
 * The GA4 tag (G-8J767XFVBX) is loaded in /public/index.html with
 * `send_page_view: false` so the SPA fires its own page_view events
 * on every React Router navigation (including the initial load).
 *
 * All helpers below safely no-op when gtag is unavailable (e.g.
 * adblockers, local dev without DNS) so they never crash the app.
 */

const GA_ID = "G-8J767XFVBX";
const CURRENCY = "CAD";

const hasGtag = () =>
    typeof window !== "undefined" && typeof window.gtag === "function";

/** Build a GA4 ecommerce `item` object from a cart line. */
const toGaItem = (item, index = 0) => ({
    item_id: String(item.id),
    item_name: item.name,
    item_category: item.category || undefined,
    item_variant: item.dosage || undefined,
    price: Number(item.price) || 0,
    quantity: item.quantity || 1,
    index,
});

/** Fire a `page_view` event for the given SPA route. */
export const trackPageView = (pathname) => {
    if (!hasGtag()) return;
    const page_path = pathname || (typeof window !== "undefined" ? window.location.pathname : "/");
    const page_location =
        typeof window !== "undefined" ? window.location.origin + page_path + window.location.search : page_path;
    const page_title = typeof document !== "undefined" ? document.title : undefined;

    window.gtag("event", "page_view", {
        page_path,
        page_location,
        page_title,
        send_to: GA_ID,
    });
};

/** Fire `add_to_cart` when a user adds a product (or variant) to the cart. */
export const trackAddToCart = (product) => {
    if (!hasGtag()) return;
    const item = toGaItem({ ...product, quantity: 1 });
    window.gtag("event", "add_to_cart", {
        currency: CURRENCY,
        value: item.price,
        items: [item],
    });
};

/** Fire `begin_checkout` when the checkout page mounts with items in cart. */
export const trackBeginCheckout = ({ items, value, referralCode }) => {
    if (!hasGtag()) return;
    if (!items || items.length === 0) return;
    window.gtag("event", "begin_checkout", {
        currency: CURRENCY,
        value: Number(value) || 0,
        items: items.map(toGaItem),
        referral_code: referralCode || undefined,
    });
};

/**
 * Fire `order_request` on a successful order-inquiry submission.
 *
 * NOTE: We use a custom `order_request` event (not GA4's built-in `purchase`)
 * because payment is collected offline via e-transfer AFTER the request is
 * submitted. Using `purchase` would inflate GA4 revenue metrics with orders
 * that may not convert to paid customers.
 */
export const trackOrderRequest = ({ transactionId, items, value, tax = 0, shipping = 0, discount = 0, coupon, referralCode }) => {
    if (!hasGtag()) return;
    window.gtag("event", "order_request", {
        transaction_id: transactionId,
        currency: CURRENCY,
        value: Number(value) || 0,
        tax: Number(tax) || 0,
        shipping: Number(shipping) || 0,
        coupon: coupon || undefined,
        items: (items || []).map(toGaItem),
        discount_amount: Number(discount) || 0,
        referral_code: referralCode || undefined,
    });
};

/** Fire when a visitor arrives via a valid ?ref= URL. */
export const trackReferralVisit = (referralCode) => {
    if (!hasGtag()) return;
    if (!referralCode) return;
    window.gtag("event", "referral_visit", {
        referral_code: referralCode,
    });
};

/** Fire a custom `telegram_click` event when the floating support widget is tapped. */
export const trackTelegramClick = (placement = "floating") => {
    if (!hasGtag()) return;
    window.gtag("event", "telegram_click", {
        method: "telegram",
        placement,
    });
};
